import { Editor } from "@tiptap/react";
import { useCallback, useEffect, useRef } from "react";
import { historyKey } from "../components/NewEmail/prosemirror-history";
import { EDITOR_KEY, SharedHistory } from "./useEmailEditorHistory";

export default function useEditorHistory(
  sharedHistoryRef: React.MutableRefObject<SharedHistory>,
  editor: Editor
) {
  const previousEditorHistoryCount = useRef<number>(0);

  const focusEditor = useCallback(() => {
    if (!editor) return;
    editor.commands.focus();
  }, [editor]);

  const redoEditor = useCallback(() => {
    if (!editor) return;
    editor.commands.redo();
  }, [editor]);

  const undoEditor = useCallback(() => {
    if (!editor) return false;
    editor.commands.undo();
  }, [editor]);

  const handleCtrlZ = useCallback(() => {
    if (sharedHistoryRef.current?.undo(EDITOR_KEY)) {
      if (previousEditorHistoryCount.current > 1) {
        previousEditorHistoryCount.current--;
      } else {
        previousEditorHistoryCount.current = 0;
      }
    }
  }, []);

  const handleCtrlY = useCallback(() => {
    if (sharedHistoryRef.current) {
      sharedHistoryRef.current.redo(EDITOR_KEY);
    }
  }, []);

  function onUpdateEditor(t: any) {
    if (!editor) return;
    if (t.transaction?.meta?.hasFirstLineAsSubject) return;
    // @ts-check
    const eventCount = historyKey.getState(editor.state).done.eventCount;

    /**
     * The t.transaction.docChanged prevents the transaction to save a history
     * when it's a ctrl+Z and the isGeneric prevents when it's a ctrl+Y
     */

    if (
      eventCount > previousEditorHistoryCount.current &&
      t.transaction.docChanged &&
      t.transaction.meta?.history$1?.redo === undefined
    ) {
      previousEditorHistoryCount.current = eventCount;
      sharedHistoryRef.current.save(EDITOR_KEY);
    }
  }

  useEffect(() => {
    if (!editor) return;
    editor.on("transaction", onUpdateEditor);
    sharedHistoryRef.current.instances[EDITOR_KEY] = {
      focus: focusEditor,
      undo: undoEditor,
      redo: redoEditor,
      save: (value) => {},
    };

    return () => {
      editor.off("transaction", onUpdateEditor);
      delete sharedHistoryRef.current.instances[EDITOR_KEY];
    };
  }, [editor, onUpdateEditor, focusEditor, redoEditor, undoEditor]);

  return {
    handleCtrlZ,
    handleCtrlY,
  };
}
